import 'whatwg-fetch'
import 'intersection-observer'

import store from './util/store'

import LazyLoad from 'vanilla-lazyload'
import {throttle, debounce} from 'throttle-debounce'

// Utils
import Loader from './util/Loader'
import Menu from './util/Menu'
import Anchor from './util/Anchor'
import Observer from './util/Observer'
import Images from './util/Images'
import ButtonsDownload from './util/ButtonsDownload'
import MouseFollow from './util/MouseFollow'
import Accordions from './util/Accordions'
import LinesFill from './util/LinesFill'
import Video from './util/Video'
import Lenis from '@studio-freight/lenis'
import CopyLink from './util/CopyLink'
import PopUpForm from './util/PopUpForm'
import Forms from './util/Forms'
import NewsEvents from './util/NewsEvents'
import Search from './util/Search'

// Renderer
import Page from './routes/Page'

// GSAP
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'
import Draggable from 'gsap/Draggable'
import ScrollToPlugin from 'gsap/ScrollToPlugin'

// Cursor
import MouseFollower from 'mouse-follower'

export default class App {
  constructor() {
    this.resize = this.resize.bind(this)
    this.scroll = this.scroll.bind(this)
    this.update = this.update.bind(this)

    this.raf = null

    this.resizeDebounced = debounce(100, this.resize)
    this.resizeThrottled = throttle(150, this.resize)

    if (!store.scrollEngine) {
      this.scrollDebounced = debounce(100, this.scroll)
      this.scrollThrottled = throttle(50, this.scroll)
    }

    store.w = {
      w: window.innerWidth,
      h: window.innerHeight,
      pR: Math.min(window.devicePixelRatio, 2)
    }

    this.start()
  }

  start() {
    this.initGSAP()
    this.initCursor()

   if (store.scrollEngine === 'lenis') this.initLenis()
    else this.initObserver()

    store.loader = new Loader()
    this.currentRenderer = new Page()
    this.menu = new Menu()
    this.anchor = new Anchor()
    this.mouseFollow = new MouseFollow()
    this.lazyLoad = new LazyLoad()
    this.newsEvents = new NewsEvents()
    this.search = new Search()

    this.events()
    this.update()

    if (!store.detect.isMobile) {
      gsap.fromTo(document.querySelector('.app'), {opacity: 0}, {
        opacity: 1,
        duration: 0.5,
        delay: 1,
        onStart: () => {
          requestAnimationFrame(this.update)
        }
      })
    } else {
      // Tablets are wider than md, where .app starts hidden (see _global.scss)
      gsap.set(document.querySelector('.app'), {opacity: 1})
    }

    this.checkAnchor()

    this.initUtils()
  }

  initPopUpForms() {
    document.querySelectorAll('.c-pop-up-form').forEach((form) => {
      const formId = form.getAttribute('data-form-id')

      if (formId) {
        // eslint-disable-next-line no-new
        new PopUpForm(formId)
      }
    })
  }

  initObserver() {
    store.observer = new Observer()
  }

  initGSAP() {
    gsap.registerPlugin(ScrollTrigger, CustomEase, Draggable, ScrollToPlugin)
  }

  initCursor() {
    MouseFollower.registerGSAP(gsap)
    store.cursor = new MouseFollower({
      skewing: 0,
      skewingText: 0,
      skewingIcon: 0,
      skewingMedia: 0,
      iconSvgSrc: document.getElementById('mf-sprite-url').innerHTML,
      stateDetection: {
        '-hidden': 'button,.e-button'
      }
    })
  }

  initLenis() {
    store.smoothScroll = new Lenis({
      lerp: 0.08,
      smooth: true,
      direction: 'vertical'
    })

    document.documentElement.classList.add('lenis')
    this.initObserver()
  }

  preload() {
    const links = document.body.querySelectorAll('a:not([target]):not([href^=\\#]):not([href^="mailto:"]):not([href^="tel:"]):not([data-taxi-ignore]):not([href*="wp-admin"]):not(.ab-item):not([href*="wp-login.php?action=logout"])')

    links.forEach((link) => {
      const href = link.getAttribute('href')

      if (href && href.startsWith('https://apply.workable.com/')) {
        const jobId = href.split('/').pop()
        const newUrl = `${window.location.origin}/job/?job_id=${jobId}`

        link.setAttribute('href', newUrl)
      }
    })

    for (let i = 0; i < links.length; i++) {
      !store.router.cache.has(links[i].href) && store.router.preload(links[i].href)
    }
  }

  resize() {
    store.w = {
      w: window.innerWidth,
      h: window.innerHeight,
      pR: Math.min(window.devicePixelRatio, 2)
    }

    this.currentRenderer.resize()
    this.menu && this.menu.resize()
  }

  scroll(e) {
    store.scrollEngine === 'lenis' && (store.smoothScroll.direction = this.oldScroll <= e ? 1 : -1)
    this.currentRenderer.scroll(e)
    this.menu && this.menu.scroll()
    this.oldScroll = e
  }

  update() {
    store.scrollEngine === 'lenis' && store.smoothScroll.raf()
    this.currentRenderer.update()
    requestAnimationFrame(this.update)
  }

  events() {
    window.addEventListener('resize', this.resizeThrottled)
    window.addEventListener('resize', this.resizeDebounced)
    window.addEventListener('orientationchange', this.resize)

   if (store.scrollEngine === 'lenis') {
      store.smoothScroll.on('scroll', ({scroll}) => this.scroll(scroll))
    } else {
      window.addEventListener('scroll', this.scrollThrottled)
      window.addEventListener('scroll', this.scrollDebounced)
    }
  }

  initUtils() {
    this.initPopUpForms()

    this.accordions = new Accordions()
    this.copyLink = new CopyLink()
    this.forms = new Forms()
    this.linesFill = new LinesFill()
    this.video = new Video()
    this.images = new Images()
    this.buttonsDownload = new ButtonsDownload()
  }

  checkAnchor(location = null) {
    const bodyClassSubmit = Array.from(document.body.classList).find((elt) => elt.includes('formsubmit'))
    let anchor = null

    if (location && location.anchor) anchor = location.anchor
    else if (bodyClassSubmit) {
      const validate = document.querySelector('#gform_confirmation_message_' + bodyClassSubmit.split('-')[1])
      const error = document.querySelector('#gform_wrapper_' + bodyClassSubmit.split('-')[1])

      if (validate) anchor = 'gform_confirmation_message_' + bodyClassSubmit.split('-')[1]
      else if (error) anchor = 'gform_wrapper_' + bodyClassSubmit.split('-')[1]
    } else {
      const idx = window.location.href.indexOf('#')

      if (idx !== -1) anchor = window.location.href.substring(idx + 1)
    }

    if (anchor) {
      const el = document.querySelector('#' + anchor)

      if (el) {
         if (store.scrollEngine === 'lenis') {
          store.smoothScroll.scrollTo(el)
        } else {
          const elRect = el.getBoundingClientRect()

          window.scrollTo(0, elRect.top)
        }
      }
    }
  }
}
